import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary: violeta sólido con leve gradiente — para CTAs principales
        primary:
          "bg-primary text-primary-foreground shadow-[inset_0_1px_0_0_hsl(0_0%_100%_/_0.12)] hover:bg-[hsl(272_91%_60%)] active:bg-[hsl(272_91%_55%)]",
        // Default: estilo Railway "View logs" — bordered sutil, fondo card
        default:
          "border border-border bg-card text-foreground hover:bg-accent hover:border-[hsl(240_9%_20%)]",
        // Ghost: sin borde, hover sutil
        ghost:
          "text-muted-foreground hover:bg-accent hover:text-foreground",
        // Subtle: relleno secundario opaco
        subtle:
          "bg-secondary text-secondary-foreground hover:bg-accent",
        // Destructive
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-[hsl(0_84%_55%)]",
        // Success: para CTAs positivos como "Entregar solución"
        success:
          "bg-success text-success-foreground shadow-[inset_0_1px_0_0_hsl(0_0%_100%_/_0.12)] hover:bg-[hsl(142_71%_40%)]",
        // Teal: usado para "Docs" en el header del test
        teal:
          "bg-[hsl(178_60%_32%)] text-white shadow-[inset_0_1px_0_0_hsl(0_0%_100%_/_0.10)] hover:bg-[hsl(178_60%_28%)]",
        // Outline danger (para "borrar" con menos peso visual)
        outlineDanger:
          "border border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10 hover:border-destructive/60",
        // Link
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-9 px-4",
        lg: "h-11 px-6 text-base",
        icon: "h-9 w-9",
        iconSm: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
